/**
 * The player's stage DOM (spec 020), shared by `StoryRuntime` and the
 * editor's `PlayerHost` (spec 009) so a preview draws the same box, scaling
 * and choice panel as the published page:
 *
 * - `PLAYER_STYLE`: the stylesheet of `.sm-root` and everything inside it;
 * - `stageFit`: the stage size, scale and offset that fit an orientation into
 *   a container, centred (letterboxed);
 * - `buildChoicePanel`: the DOM choice panel (layout, position, buttons, timer);
 * - `choicePanelBottom` / `stackChoicePanel`: a `bottom` panel sits 10 px above
 *   the highest bottom dialog box the scene drew (`SceneRuntime.getDialogBoxes()`).
 */

import type { ChoiceOption, ChoiceSettings, Orientation } from '../@types';
import { STAGE_SIZES } from './evaluateFrame';
import type { DialogBoxRect } from './dialogBox';
import { sizeCanvasBuffer } from './canvasBuffer';

/** Distance of a `bottom` panel from the stage bottom when no bottom dialog box is drawn. */
export const PANEL_BOTTOM = 16;
/** Gap between a `bottom` panel's lower edge and the top of the bottom dialog box below it. */
export const PANEL_DIALOG_GAP = 10;

export const PLAYER_STYLE = `
.sm-root{position:relative;width:100%;height:100%;overflow:hidden;background:#000;font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif}
.sm-root:focus{outline:none}
.sm-stage{position:absolute;left:0;top:0;transform-origin:0 0;overflow:hidden;background:#000}
.sm-canvas{position:absolute;left:0;top:0;width:100%;height:100%;display:block}
.sm-sr{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0}
.sm-panel{position:absolute;left:0;right:0;display:flex;gap:10px;padding:0 16px;z-index:2;box-sizing:border-box}
.sm-panel.sm-list{flex-direction:column}
.sm-panel.sm-grid{flex-direction:row;flex-wrap:wrap}
.sm-panel.sm-grid .sm-choice{flex:1 1 40%}
.sm-choice{font:600 17px/1.3 inherit;font-family:inherit;padding:12px 16px;border:0;border-radius:10px;background:rgba(255,255,255,.94);color:#111;cursor:pointer;text-align:center;box-shadow:0 2px 8px rgba(0,0,0,.35)}
.sm-choice:hover{background:#fff}
.sm-choice:disabled{cursor:default}
.sm-choice:focus-visible{outline:3px solid #4da3ff;outline-offset:2px}
.sm-timer{align-self:center;min-width:36px;padding:4px 10px;border-radius:999px;background:rgba(0,0,0,.65);color:#fff;font:600 15px/1.4 inherit;font-family:inherit;text-align:center}
.sm-loading{position:absolute;right:12px;bottom:12px;z-index:3;padding:6px 12px;border-radius:999px;background:rgba(0,0,0,.6);color:#fff;font-size:14px}
.sm-end{position:absolute;inset:0;z-index:4;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;background:rgba(0,0,0,.6);color:#fff}
.sm-end p{margin:0;font-size:20px;font-weight:600}
.sm-button{font:600 18px/1 inherit;font-family:inherit;padding:14px 28px;border:0;border-radius:999px;background:#fff;color:#111;cursor:pointer}
.sm-button:focus-visible{outline:3px solid #4da3ff;outline-offset:3px}
.sm-start{position:absolute;inset:0;z-index:5;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.45)}
.sm-message{position:absolute;inset:0;z-index:6;display:flex;align-items:center;justify-content:center;padding:24px;text-align:center;color:#fff;font-size:18px;background:#000}
`;

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, attrs: Record<string, string> = {}): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  return node;
}

export interface StageFit {
  /** The stage in stage px (the orientation's stage size; the buffer is this times the pixel ratio). */
  width: number;
  height: number;
  /** Scale and offset that fit it into the container, centred. */
  scale: number;
  x: number;
  y: number;
}

/** Fit an orientation's stage into a container of `cw`×`ch` CSS px, centred. */
export function stageFit(orientation: Orientation, cw: number, ch: number): StageFit {
  const stage = STAGE_SIZES[orientation] ?? STAGE_SIZES.portrait;
  let k = 1;
  if (cw > 0 && ch > 0) k = Math.min(cw / stage.width, ch / stage.height);
  else if (cw > 0) k = cw / stage.width;
  const x = cw > 0 ? (cw - stage.width * k) / 2 : 0;
  const y = ch > 0 ? (ch - stage.height * k) / 2 : 0;
  return { width: stage.width, height: stage.height, scale: k, x, y };
}

/**
 * Apply a `stageFit` to the stage element and size the canvas buffer: the
 * stage size times the device pixel ratio (`canvasBuffer`), the canvas's CSS
 * size staying the stage's. True when the buffer changed (it is blank: the
 * caller redraws).
 */
export function applyStageFit(stage: HTMLElement, canvas: HTMLCanvasElement, orientation: Orientation, fit: StageFit): boolean {
  stage.style.width = `${fit.width}px`;
  stage.style.height = `${fit.height}px`;
  stage.style.transform = `translate(${fit.x}px, ${fit.y}px) scale(${fit.scale})`;
  stage.setAttribute('data-orientation', orientation);
  return sizeCanvasBuffer(canvas, fit.width, fit.height);
}

export interface ChoicePanel {
  panel: HTMLDivElement;
  buttons: HTMLButtonElement[];
  /** The countdown element when the scene has a timer. */
  timerEl: HTMLSpanElement | null;
  /** The timer in seconds, 0 when there is none. */
  seconds: number;
}

/**
 * The choice panel (spec 008/020): options in the given (display) order, list
 * or grid, at the bottom or centred, with a countdown chip when timed.
 * `onSelect` is called with the clicked option's id.
 */
export function buildChoicePanel(
  choices: ChoiceOption[],
  settings: ChoiceSettings,
  onSelect: (choiceId: string) => void,
): ChoicePanel {
  const layout = settings.layout === 'grid' ? 'grid' : 'list';
  const position = settings.position === 'center' ? 'center' : 'bottom';
  const panel = el('div', `sm-panel sm-${layout}`, {
    'data-story-choice-panel': '',
    'data-layout': layout,
    'data-position': position,
    role: 'group',
    'aria-label': 'Choices',
  });
  if (position === 'center') {
    panel.style.top = '50%';
    panel.style.transform = 'translateY(-50%)';
  } else {
    panel.style.bottom = `${PANEL_BOTTOM}px`;
  }
  const buttons = choices.map((option) => {
    const button = el('button', 'sm-choice', { type: 'button', 'data-choice-id': option.id });
    button.textContent = option.text;
    button.addEventListener('click', () => onSelect(option.id));
    return button;
  });
  panel.append(...buttons);
  const seconds = settings.timer != null && settings.timer > 0 ? settings.timer : 0;
  let timerEl: HTMLSpanElement | null = null;
  if (seconds > 0) {
    timerEl = el('span', 'sm-timer', { 'data-story-choice-timer': '', 'aria-label': 'Seconds left' });
    timerEl.textContent = String(Math.ceil(seconds));
    panel.appendChild(timerEl);
  }
  return { panel, buttons, timerEl, seconds };
}

/** Disable a choice panel's buttons: a selection was made and the scene's exit plays (spec 015). */
export function lockChoicePanel(panel: HTMLElement): void {
  panel.setAttribute('aria-disabled', 'true');
  panel.querySelectorAll('button').forEach((b) => { (b as HTMLButtonElement).disabled = true; });
}

/**
 * The CSS `bottom` (stage px) of a `bottom` choice panel: `PANEL_DIALOG_GAP`
 * above the top of the highest bottom box in `boxes` (a one-line box at
 * y 577 on the 667 px portrait stage gives 100, a three-line one at 553 gives
 * 124), `PANEL_BOTTOM` with none. Pure; the editor canvas can reuse it with
 * `layoutDialogBox` rects (017).
 */
export function choicePanelBottom(
  boxes: readonly Pick<DialogBoxRect, 'position' | 'y'>[],
  stageHeight: number,
): number {
  let top = Infinity;
  for (const b of boxes) if (b.position !== 'top' && b.y < top) top = b.y;
  return Number.isFinite(top) ? Math.max(PANEL_BOTTOM, stageHeight - top + PANEL_DIALOG_GAP) : PANEL_BOTTOM;
}

/** Stack a `bottom` panel on the dialog boxes of the frame last drawn (`SceneRuntime.getDialogBoxes()`). */
export function stackChoicePanel(panel: HTMLElement, boxes: readonly DialogBoxRect[], stageHeight: number): void {
  if (panel.dataset.position !== 'bottom') return;
  const bottom = `${choicePanelBottom(boxes, stageHeight)}px`;
  if (panel.style.bottom !== bottom) panel.style.bottom = bottom;
}

/** Focus without scrolling the host page (older browsers ignore the option). */
export function focusQuietly(node: HTMLElement | undefined | null): void {
  if (!node) return;
  try {
    node.focus({ preventScroll: true });
  } catch {
    node.focus();
  }
}
